import React, { useMemo } from 'react';
import {
  Autocomplete,
  Box,
  FormControl,
  Grid,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import ContactKindChip from './ContactKindChip';
import OpportunityNotesField from './OpportunityNotesField';
import { useAuth } from '../contexts/AuthContext';
import {
  Client,
  ClientPoc,
  Office,
  OpportunityPipelineStatus,
  User,
} from '../types';
import {
  OPPORTUNITY_STAGE_CONFIG,
  OpportunityFieldKey,
  PROJECT_IDENTIFIED_OPTIONS,
  SALES_PROJECT_TYPES,
} from '../utils/commercialPlanning';
import {
  OpportunityFormValues,
  getFieldLabel,
  isFieldRequired,
} from '../utils/opportunityForm';
import {
  clientPocsWithEmail,
  filterClientPocOptions,
  normalizePocEmail,
  pocSelectionEmail,
} from '../utils/clientPocs';
import { getZoomCompensatedAutocompleteSlotProps } from '../utils/zoomCompensation';
import {
  ContactAutocompleteOption,
  buildContactAutocompleteOptions,
  getContactOptionLabel,
  groupContactOption,
  isCreateLeadOption,
  sortContactsForPicker,
} from '../utils/contactPicker';

type OpportunityFormFieldsProps = {
  form: OpportunityFormValues;
  setForm: React.Dispatch<React.SetStateAction<OpportunityFormValues>>;
  visibleStages: OpportunityPipelineStatus[];
  visibleFields?: OpportunityFieldKey[];
  clients: Client[];
  salesUsers: User[];
  offices: Office[];
  companyInputValue: string;
  setCompanyInputValue: (value: string) => void;
  onCompanySelection: (option: ContactAutocompleteOption | null) => void | Promise<void>;
  findExistingClientByName: (clients: Client[], candidateName: string) => Client | undefined;
  creatingContact: boolean;
  showProbability?: boolean;
  notesExpanded?: boolean;
  notesFullscreen?: boolean;
  onToggleNotesFullscreen?: () => void;
  invalid?: (key: string) => boolean;
  onFieldEdit?: (key: string) => void;
};

const OpportunityFormFields: React.FC<OpportunityFormFieldsProps> = ({
  form,
  setForm,
  visibleStages,
  visibleFields,
  clients,
  salesUsers,
  offices,
  companyInputValue,
  setCompanyInputValue,
  onCompanySelection,
  findExistingClientByName,
  creatingContact,
  showProbability = false,
  notesExpanded = false,
  notesFullscreen = false,
  onToggleNotesFullscreen,
  invalid,
  onFieldEdit,
}) => {
  const { user } = useAuth();
  const sortedClients = useMemo(() => sortContactsForPicker(clients), [clients]);
  const selectedClient = useMemo(
    () => (form.client_id === '' ? null : clients.find((client) => client.id === form.client_id) || null),
    [clients, form.client_id],
  );
  const recommendedPocs = useMemo(() => clientPocsWithEmail(selectedClient), [selectedClient]);
  const selectedContactValue = useMemo<ClientPoc | string | null>(() => {
    const email = form.email.trim();
    if (!email) return null;
    return recommendedPocs.find(
      (poc) => normalizePocEmail(poc.email || '') === normalizePocEmail(email),
    ) || email;
  }, [form.email, recommendedPocs]);

  const shouldShowField = (stage: OpportunityPipelineStatus, field: OpportunityFieldKey) => {
    if (visibleFields) return visibleFields.includes(field);
    return OPPORTUNITY_STAGE_CONFIG[stage].fields.includes(field);
  };

  const renderField = (stage: OpportunityPipelineStatus, field: OpportunityFieldKey) => {
    if (!shouldShowField(stage, field)) return null;
    const required = isFieldRequired(field, form.status);
    const err = Boolean(invalid?.(field));
    const touch = () => onFieldEdit?.(field);

    switch (field) {
      case 'title':
        return (
          <Grid item xs={12} md={6} key={field}>
            <TextField
              label={getFieldLabel(field)}
              fullWidth
              required={required}
              error={err}
              value={form.title}
              onChange={(e) => {
                touch();
                setForm((prev) => ({ ...prev, title: e.target.value }));
              }}
            />
          </Grid>
        );
      case 'client_id':
        return (
          <Grid item xs={12} md={6} key={field}>
            <Autocomplete
              fullWidth
              openOnFocus
              autoHighlight
              forcePopupIcon
              loading={creatingContact}
              options={sortedClients}
              value={selectedClient}
              inputValue={companyInputValue}
              getOptionLabel={getContactOptionLabel}
              groupBy={groupContactOption}
              isOptionEqualToValue={(option, value) => {
                if (!value || isCreateLeadOption(option) || isCreateLeadOption(value)) return false;
                return option.id === value.id;
              }}
              filterOptions={(_, state) => {
                const query = state.inputValue.trim().toLowerCase();
                const filtered = sortedClients.filter(
                  (client) => !query || client.name.toLowerCase().includes(query),
                );
                return buildContactAutocompleteOptions(
                  filtered,
                  state.inputValue,
                  (name) => Boolean(findExistingClientByName(clients, name)),
                );
              }}
              onChange={(_, newValue) => {
                touch();
                void onCompanySelection(newValue);
              }}
              onInputChange={(_, newInputValue, reason) => {
                if (reason === 'input' || reason === 'clear') {
                  setCompanyInputValue(newInputValue);
                  if (reason === 'clear') {
                    touch();
                    setForm((prev) => ({ ...prev, client_id: '' }));
                  }
                }
              }}
              slotProps={getZoomCompensatedAutocompleteSlotProps()}
              renderGroup={(params) => (
                <li key={params.key}>
                  {params.group ? (
                    <ListSubheader
                      component="div"
                      sx={{
                        lineHeight: '32px',
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        bgcolor: 'background.paper',
                      }}
                    >
                      {params.group}
                    </ListSubheader>
                  ) : null}
                  <ul style={{ padding: 0 }}>{params.children}</ul>
                </li>
              )}
              renderOption={(props, option) => {
                if (isCreateLeadOption(option)) {
                  return (
                    <li {...props} key={`create-${option.name}`}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Create lead &ldquo;{option.name}&rdquo;
                      </Typography>
                    </li>
                  );
                }
                return (
                  <li {...props} key={option.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {option.name}
                      </Typography>
                      <ContactKindChip kind={option.kind ?? 'client'} />
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={getFieldLabel(field)}
                  error={err}
                  placeholder="Search clients and leads…"
                  helperText="Optional. Create a lead if the company is not listed."
                />
              )}
            />
          </Grid>
        );
      case 'contact_name':
        return (
          <Grid item xs={12} md={6} key={field}>
            <TextField
              label={getFieldLabel(field)}
              fullWidth
              value={form.contact_name}
              error={err}
              onChange={(e) => {
                touch();
                setForm((prev) => ({ ...prev, contact_name: e.target.value }));
              }}
            />
          </Grid>
        );
      case 'email':
        return (
          <Grid item xs={12} md={6} key={field}>
            <Autocomplete
              fullWidth
              freeSolo
              openOnFocus
              autoHighlight
              forcePopupIcon
              options={recommendedPocs}
              value={selectedContactValue}
              inputValue={form.email}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : option.email || ''}
              isOptionEqualToValue={(option, value) =>
                normalizePocEmail(pocSelectionEmail(option)) ===
                normalizePocEmail(pocSelectionEmail(value))}
              filterOptions={(options, { inputValue }) => {
                const query = inputValue.trim();
                const isExactPocEmail = options.some(
                  (poc) => normalizePocEmail(pocSelectionEmail(poc)) === normalizePocEmail(query),
                );
                if (!query || isExactPocEmail) return options;
                return filterClientPocOptions(options, inputValue);
              }}
              noOptionsText={
                !selectedClient
                  ? 'Select a company to see their contacts'
                  : recommendedPocs.length === 0
                    ? 'No contacts with email for this company'
                    : 'No matching contacts'
              }
              onChange={(_, newValue) => {
                touch();
                setForm((prev) => ({
                  ...prev,
                  email: pocSelectionEmail(newValue ?? ''),
                }));
              }}
              onInputChange={(_, newInputValue, reason) => {
                if (reason === 'input' || reason === 'clear') {
                  touch();
                  setForm((prev) => ({ ...prev, email: newInputValue }));
                }
              }}
              slotProps={getZoomCompensatedAutocompleteSlotProps()}
              renderOption={(props, option) => {
                const poc = typeof option === 'string'
                  ? { full_name: '', email: option, job_position: undefined }
                  : option;
                return (
                  <li {...props}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.25 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {poc.full_name || poc.email}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[poc.job_position, poc.full_name ? poc.email : null]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={getFieldLabel(field)}
                  type="email"
                  error={err}
                  autoComplete="email"
                  placeholder="name@company.com"
                  helperText="Optional. Must be valid if filled."
                />
              )}
            />
          </Grid>
        );
      case 'source':
        return (
          <Grid item xs={12} md={6} key={field}>
            <TextField
              label={getFieldLabel(field)}
              fullWidth
              value={form.source}
              error={err}
              onChange={(e) => {
                touch();
                setForm((prev) => ({ ...prev, source: e.target.value }));
              }}
            />
          </Grid>
        );
      case 'commercial_owner_id':
        return (
          <Grid item xs={12} md={6} key={field}>
            <FormControl fullWidth required={required} error={err}>
              <InputLabel>{getFieldLabel(field)}</InputLabel>
              <Select
                label={getFieldLabel(field)}
                value={form.commercial_owner_id}
                onChange={(e) => {
                  touch();
                  setForm((prev) => ({
                    ...prev,
                    commercial_owner_id: e.target.value as number | '',
                  }));
                }}
              >
                <MenuItem value="">None</MenuItem>
                {salesUsers.map((user) => (
                  <MenuItem key={user.id} value={user.id}>{user.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        );
      case 'office_id':
        return (
          <Grid item xs={12} md={6} key={field}>
            <FormControl fullWidth required={required} error={err}>
              <InputLabel>{getFieldLabel(field)}</InputLabel>
              <Select
                label={getFieldLabel(field)}
                value={form.office_id}
                onChange={(e) => {
                  touch();
                  setForm((prev) => ({
                    ...prev,
                    office_id: e.target.value as number | '',
                  }));
                }}
              >
                <MenuItem value="">None</MenuItem>
                {offices.map((office) => (
                  <MenuItem key={office.id} value={office.id}>
                    {office.name || office.code || office.country}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        );
      case 'notes':
        return null;
      case 'project_type':
        return (
          <Grid item xs={12} md={6} key={field}>
            <FormControl fullWidth error={err}>
              <InputLabel>{getFieldLabel(field)}</InputLabel>
              <Select
                label={getFieldLabel(field)}
                value={form.project_type}
                onChange={(e) => {
                  touch();
                  setForm((prev) => ({
                    ...prev,
                    project_type: e.target.value,
                  }));
                }}
              >
                <MenuItem value="">None</MenuItem>
                {SALES_PROJECT_TYPES.map((projectType) => (
                  <MenuItem key={projectType} value={projectType}>
                    {projectType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        );
      case 'project_identified':
        return (
          <Grid item xs={12} md={6} key={field}>
            <FormControl fullWidth error={err}>
              <InputLabel>{getFieldLabel(field)}</InputLabel>
              <Select
                label={getFieldLabel(field)}
                value={form.project_identified}
                onChange={(e) => {
                  touch();
                  setForm((prev) => ({
                    ...prev,
                    project_identified: e.target.value as OpportunityFormValues['project_identified'],
                  }));
                }}
              >
                <MenuItem value="">None</MenuItem>
                {PROJECT_IDENTIFIED_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        );
      case 'project_location':
        return (
          <Grid item xs={12} md={6} key={field}>
            <TextField
              label={getFieldLabel(field)}
              fullWidth
              value={form.project_location}
              error={err}
              onChange={(e) => {
                touch();
                setForm((prev) => ({ ...prev, project_location: e.target.value }));
              }}
            />
          </Grid>
        );
      case 'expected_decision_date':
        return (
          <Grid item xs={12} md={6} key={field}>
            <TextField
              label={getFieldLabel(field)}
              fullWidth
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.expected_decision_date}
              error={err}
              onChange={(e) => {
                touch();
                setForm((prev) => ({ ...prev, expected_decision_date: e.target.value }));
              }}
            />
          </Grid>
        );
      case 'estimated_value':
        return (
          <Grid item xs={12} md={6} key={field}>
            <TextField
              label={getFieldLabel(field)}
              fullWidth
              type="number"
              value={form.estimated_value}
              error={err}
              onChange={(e) => {
                touch();
                setForm((prev) => ({ ...prev, estimated_value: e.target.value }));
              }}
            />
          </Grid>
        );
      case 'opportunity_value':
        return (
          <Grid item xs={12} md={6} key={field}>
            <TextField
              label={getFieldLabel(field)}
              fullWidth
              type="number"
              value={form.opportunity_value}
              error={err}
              onChange={(e) => {
                touch();
                setForm((prev) => ({ ...prev, opportunity_value: e.target.value }));
              }}
            />
          </Grid>
        );
      case 'expected_closing_date':
        return (
          <Grid item xs={12} md={6} key={field}>
            <TextField
              label={getFieldLabel(field)}
              fullWidth
              type="date"
              required={required}
              InputLabelProps={{ shrink: true }}
              value={form.expected_closing_date}
              error={err}
              onChange={(e) => {
                touch();
                setForm((prev) => ({ ...prev, expected_closing_date: e.target.value }));
              }}
            />
          </Grid>
        );
      case 'proposal_amount':
        return (
          <Grid item xs={12} md={6} key={field}>
            <TextField
              label={getFieldLabel(field)}
              fullWidth
              type="number"
              value={form.proposal_amount}
              error={err}
              onChange={(e) => {
                touch();
                setForm((prev) => ({ ...prev, proposal_amount: e.target.value }));
              }}
            />
          </Grid>
        );
      case 'proposal_date':
        return (
          <Grid item xs={12} md={6} key={field}>
            <TextField
              label={getFieldLabel(field)}
              fullWidth
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.proposal_date}
              error={err}
              onChange={(e) => {
                touch();
                setForm((prev) => ({ ...prev, proposal_date: e.target.value }));
              }}
            />
          </Grid>
        );
      case 'next_action_date': {
        const otherDateWarning = form.status === 'other' && !form.next_action_date.trim();
        return (
          <Grid item xs={12} md={6} key={field}>
            <TextField
              label={getFieldLabel(field)}
              fullWidth
              type="date"
              required={required}
              InputLabelProps={{ shrink: true }}
              value={form.next_action_date}
              error={err}
              helperText={
                otherDateWarning
                  ? 'No next action is scheduled, so this lead will sit at the bottom of the list.'
                  : undefined
              }
              FormHelperTextProps={
                otherDateWarning ? { sx: { color: 'warning.main' } } : undefined
              }
              onChange={(e) => {
                touch();
                setForm((prev) => ({ ...prev, next_action_date: e.target.value }));
              }}
            />
          </Grid>
        );
      }
      case 'lost_reason':
        return (
          <Grid item xs={12} key={field}>
            <TextField
              label={getFieldLabel(field)}
              fullWidth
              required={required}
              multiline
              minRows={2}
              value={form.lost_reason}
              error={err}
              onChange={(e) => {
                touch();
                setForm((prev) => ({ ...prev, lost_reason: e.target.value }));
              }}
            />
          </Grid>
        );
      case 'reason':
        return (
          <Grid item xs={12} key={field}>
            <TextField
              label={getFieldLabel(field)}
              fullWidth
              required={required}
              multiline
              minRows={2}
              value={form.reason}
              error={err}
              onChange={(e) => {
                touch();
                setForm((prev) => ({ ...prev, reason: e.target.value }));
              }}
            />
          </Grid>
        );
      case 'final_value':
        return (
          <Grid item xs={12} md={6} key={field}>
            <TextField
              label={getFieldLabel(field)}
              fullWidth
              type="number"
              value={form.final_value}
              error={err}
              onChange={(e) => {
                touch();
                setForm((prev) => ({ ...prev, final_value: e.target.value }));
              }}
            />
          </Grid>
        );
      case 'closing_date':
        return (
          <Grid item xs={12} md={6} key={field}>
            <TextField
              label={getFieldLabel(field)}
              fullWidth
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.closing_date}
              error={err}
              onChange={(e) => {
                touch();
                setForm((prev) => ({ ...prev, closing_date: e.target.value }));
              }}
            />
          </Grid>
        );
      default:
        return null;
    }
  };

  const seenFields = new Set<OpportunityFieldKey>();
  const notesVisible = visibleFields
    ? visibleFields.includes('notes')
    : visibleStages.some((stage) => OPPORTUNITY_STAGE_CONFIG[stage].fields.includes('notes'));

  return (
    <Grid
      container
      spacing={2}
      sx={notesFullscreen ? { height: '100%', flex: 1, minHeight: 0, alignContent: 'stretch' } : undefined}
    >
      {!notesFullscreen && visibleStages.map((stage) => {
        const stageFields = OPPORTUNITY_STAGE_CONFIG[stage].fields.filter((field) => {
          if (field === 'notes') return false;
          if (!shouldShowField(stage, field)) return false;
          if (seenFields.has(field)) return false;
          seenFields.add(field);
          return true;
        });
        if (stageFields.length === 0) return null;

        return (
          <React.Fragment key={stage}>
            {!visibleFields && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: stage === visibleStages[0] ? 0 : 1 }}>
                  {OPPORTUNITY_STAGE_CONFIG[stage].label}
                </Typography>
              </Grid>
            )}
            {stageFields.map((field) => renderField(stage, field))}
          </React.Fragment>
        );
      })}

      {!notesFullscreen && showProbability && (
        <Grid item xs={12} md={6}>
          <TextField
            label="Probability To Win (%)"
            fullWidth
            type="number"
            value={form.probability_to_win}
            onChange={(e) => setForm((prev) => ({ ...prev, probability_to_win: e.target.value }))}
          />
        </Grid>
      )}

      {notesVisible && (
        <Grid
          item
          xs={12}
          key="lead-notes"
          sx={notesFullscreen ? { flex: 1, minHeight: 0, display: 'flex', height: '100%' } : undefined}
        >
          <OpportunityNotesField
            notes={form.notes}
            author={user?.name || ''}
            variant={notesExpanded ? 'expanded' : 'compact'}
            fullscreen={notesFullscreen}
            onToggleFullscreen={onToggleNotesFullscreen}
            onNotesChange={(value) => setForm((prev) => ({ ...prev, notes: value }))}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default OpportunityFormFields;
